import React, { useState, useEffect } from 'react';
import {
  getTenants,
  createTenant,
  updateTenant,
  deactivateTenant,
  activateTenant
} from '../../services/adminApiService';
import TenantForm from './TenantForm';

const TenantManagement = () => {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    loadTenants();
  }, [includeInactive]);

  async function loadTenants() {
    try {
      setLoading(true);
      setError(null);
      const response = await getTenants(includeInactive);
      setTenants(response.tenants || []);
    } catch (err) {
      console.error('Error loading tenants:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function handleCreate() {
    setEditingTenant(null);
    setShowForm(true);
  }

  function handleEdit(tenant) {
    setEditingTenant(tenant);
    setShowForm(true);
  }

  function handleCancelForm() {
    setShowForm(false);
    setEditingTenant(null);
  }

  async function handleSubmitForm(data) {
    try {
      setError(null);
      setSuccessMessage('');

      if (editingTenant) {
        await updateTenant(editingTenant.id, data);
        setSuccessMessage('Tenant updated successfully!');
      } else {
        await createTenant(data);
        setSuccessMessage('Tenant created successfully!');
      }

      setShowForm(false);
      setEditingTenant(null);
      loadTenants();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error submitting tenant:', err);
      setError(err.message);
    }
  }

  async function handleDeactivate(id) {
    if (!window.confirm('Are you sure you want to deactivate this tenant?')) {
      return;
    }

    try {
      setError(null);
      await deactivateTenant(id);
      setSuccessMessage('Tenant deactivated successfully!');
      loadTenants();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error deactivating tenant:', err);
      setError(err.message);
    }
  }

  async function handleActivate(id) {
    try {
      setError(null);
      await activateTenant(id);
      setSuccessMessage('Tenant activated successfully!');
      loadTenants();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error activating tenant:', err);
      setError(err.message);
    }
  }

  if (loading) {
    return <div className="admin-loading">Loading tenants...</div>;
  }

  return (
    <div className="tenant-management">
      <div className="management-header">
        <h2>Tenant Management</h2>
        <div className="management-actions">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
            Show Inactive
          </label>
          <button onClick={handleCreate} className="btn btn-primary">
            Add New Tenant
          </button>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {successMessage && <div className="alert alert-success">{successMessage}</div>}

      {showForm && (
        <div className="form-modal">
          <div className="form-modal-content">
            <TenantForm
              tenant={editingTenant}
              onSubmit={handleSubmitForm}
              onCancel={handleCancelForm}
            />
          </div>
        </div>
      )}

      <div className="tenant-list">
        {tenants.length === 0 ? (
          <div className="empty-state">
            No tenants found. Click "Add New Tenant" to create one.
          </div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Company Name</th>
                <th>Onshape Company ID</th>
                <th>API URL</th>
                <th>Status</th>
                <th>Last Accessed</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tenants.map((tenant) => (
                <tr key={tenant.id} className={!tenant.is_active ? 'inactive-row' : ''}>
                  <td>{tenant.id}</td>
                  <td>{tenant.company_name}</td>
                  <td><code>{tenant.onshape_company_id}</code></td>
                  <td><code className="small-code">{tenant.onshape_api_url}</code></td>
                  <td>
                    <span className={`status-badge ${tenant.is_active ? 'active' : 'inactive'}`}>
                      {tenant.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    {tenant.last_accessed_at
                      ? new Date(tenant.last_accessed_at).toLocaleString()
                      : 'Never'}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        onClick={() => handleEdit(tenant)}
                        className="btn btn-small btn-secondary"
                        disabled={!tenant.is_active}
                      >
                        Edit
                      </button>
                      {tenant.is_active ? (
                        <button
                          onClick={() => handleDeactivate(tenant.id)}
                          className="btn btn-small btn-danger"
                        >
                          Deactivate
                        </button>
                      ) : (
                        <button
                          onClick={() => handleActivate(tenant.id)}
                          className="btn btn-small btn-success"
                        >
                          Activate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default TenantManagement;
